import { useEffect, useMemo, useState } from 'react';

import type { HeaderFooterButton } from '@/components/page-sections/types';
import type {
  FooterElements,
  FooterSettings,
  FooterSlotConfig,
  HeaderSlotConfig,
  SiteHeaderSlotConfig,
} from '@/components/slot-settings/types';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  findPageEditorSectionElement,
  PAGE_EDITOR_SCROLL_CONTAINER_SELECTOR,
} from '@/lib/page-editor/scroll-to-section';
import { getDocument } from '@/lib/utils';
import { PAGE_EDITOR_HISTORY_TARGET_IDS } from '@/stores/page-editor/constants';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { useSiteStylesState, useSiteStylesStore } from '@/stores/site-styles-store';
import { type FooterSlot, type HeaderSlot, SlotName } from '@/types/page';

export function mergePreviewSlot<TSlot extends object, TDraft extends object>(
  slot: TSlot | undefined,
  draft: TDraft | null,
): TSlot | null {
  if (!slot) return null;
  return draft ? ({ ...slot, ...draft } as TSlot) : slot;
}

const DEFAULT_FOOTER_ELEMENTS: FooterElements = {
  showLogo: true,
  showText: true,
  showSocialLinks: false,
  showNavigationLinks: true,
  showUniversalDisclaimer: false,
};

const DEFAULT_FOOTER_SETTINGS: FooterSettings = {
  fullWidth: false,
  spacing: 'medium',
};

export function normalizeFooterElements(
  elements: Partial<FooterElements> | undefined,
): FooterElements {
  return { ...DEFAULT_FOOTER_ELEMENTS, ...elements };
}

export function normalizeFooterSettings(
  settings: Partial<FooterSettings> | undefined,
): FooterSettings {
  return { ...DEFAULT_FOOTER_SETTINGS, ...settings };
}

export function buildHeaderConfig(
  headerSlot: HeaderSlot | undefined,
  pageHeaderVisibleOverride: boolean | undefined,
): HeaderSlotConfig {
  return {
    visible: pageHeaderVisibleOverride ?? true,
    variant: (headerSlot?.variant as HeaderSlotConfig['variant']) ?? 'nav-left',
    elements: (headerSlot?.elements ?? {}) as HeaderSlotConfig['elements'],
    settings: (headerSlot?.settings ?? {}) as HeaderSlotConfig['settings'],
    primaryButton: headerSlot?.primaryButton,
    secondaryButton: headerSlot?.secondaryButton,
  };
}

export function buildFooterConfig(
  footerSlot: FooterSlot | undefined,
  pageFooterVisibleOverride: boolean | undefined,
): FooterSlotConfig {
  return {
    visible: pageFooterVisibleOverride ?? true,
    variant: (footerSlot?.variant as FooterSlotConfig['variant']) ?? 'default',
    elements: normalizeFooterElements(footerSlot?.elements),
    settings: normalizeFooterSettings(footerSlot?.settings),
    socialLinks: footerSlot?.socialLinks ?? [],
    text: footerSlot?.text,
    socialMediaHeading: footerSlot?.socialMediaHeading,
  };
}

export function usePageSlotEditor() {
  const isMobile = useIsMobile();
  const page = usePageEditorStore((state) => state.page);
  const slots = page?.slots ?? null;
  const editingSlot = usePageEditorStore((state) => state.editingSlot);
  const setSlotVisibility = usePageEditorStore((state) => state.setSlotVisibility);

  const [headerDraft, setHeaderDraft] = useState<HeaderSlotConfig | null>(null);
  const [footerDraft, setFooterDraft] = useState<FooterSlotConfig | null>(null);

  const isHeaderEditing = editingSlot === SlotName.HEADER;
  const isFooterEditing = editingSlot === SlotName.FOOTER;
  const isStructuralSlotEditing = isHeaderEditing || isFooterEditing;

  // Scroll lock while a structural slot panel is open.
  useEffect(() => {
    if (!isStructuralSlotEditing) return;

    const doc = getDocument();
    if (!doc) return;

    const scrollContainer = doc.querySelector<HTMLElement>(PAGE_EDITOR_SCROLL_CONTAINER_SELECTOR);
    const previousContainerOverflowY = scrollContainer?.style.overflowY ?? '';
    const previousContainerPaddingRight = scrollContainer?.style.paddingRight ?? '';

    if (scrollContainer) {
      const scrollbarGutterWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
      const computedPaddingRight =
        Number.parseFloat(getComputedStyle(scrollContainer).paddingRight) || 0;
      scrollContainer.style.overflowY = 'hidden';
      if (isMobile === false && scrollbarGutterWidth > 0) {
        scrollContainer.style.paddingRight = `${computedPaddingRight + scrollbarGutterWidth}px`;
      }
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.style.overflowY = previousContainerOverflowY;
        scrollContainer.style.paddingRight = previousContainerPaddingRight;
      }
    };
  }, [isMobile, isStructuralSlotEditing]);

  useIsomorphicLayoutEffect(() => {
    if (!isFooterEditing) return;

    const scrollContainer = getDocument()?.querySelector<HTMLElement>(
      PAGE_EDITOR_SCROLL_CONTAINER_SELECTOR,
    );
    const footer = findPageEditorSectionElement(PAGE_EDITOR_HISTORY_TARGET_IDS.footer);
    if (!scrollContainer || !footer) return;

    const anchorToBottom = () => {
      scrollContainer.scrollTop = Math.max(
        0,
        scrollContainer.scrollHeight - scrollContainer.clientHeight,
      );
    };

    anchorToBottom();

    // Layout switches and late-loading logos can resize the footer while scrolling is locked.
    const observer = new ResizeObserver(anchorToBottom);
    observer.observe(footer);
    observer.observe(scrollContainer);

    return () => observer.disconnect();
  }, [isFooterEditing]);

  // Slot-settings (variant/elements/settings/buttons) are autosaved by
  // useSiteStylesStore.setDraftHeader/Footer via persistSlot. This handler only
  // covers the page-level visibility toggle, which is captured in page history
  // and persisted via the normal draft autosave so it goes through Publish.
  const saveHeaderSettings = async (config: HeaderSlotConfig) => {
    const currentVisible = page?.metadata?.slots?.header?.visible ?? true;
    if (config.visible !== currentVisible) {
      setSlotVisibility('header', config.visible);
    }
  };

  const saveFooterSettings = async (config: FooterSlotConfig) => {
    const currentVisible = page?.metadata?.slots?.footer?.visible ?? true;
    if (config.visible !== currentVisible) {
      setSlotVisibility('footer', config.visible);
    }
  };

  // Button editor actions: preview (live), save (persist), revert (cancel)
  const headerButtonActions = useMemo(
    () => ({
      onPreview: (position: 'first' | 'second', button: HeaderFooterButton) => {
        const key = position === 'first' ? 'primaryButton' : 'secondaryButton';
        setHeaderDraft((prev) => {
          const base =
            prev ?? buildHeaderConfig(slots?.header, page?.metadata?.slots?.header?.visible);
          return { ...base, [key]: button };
        });
      },
      onSave: (position: 'first' | 'second', button: HeaderFooterButton) => {
        const key = position === 'first' ? 'primaryButton' : 'secondaryButton';
        const currentConfig = buildHeaderConfig(
          slots?.header,
          page?.metadata?.slots?.header?.visible,
        );
        const { visible: _visible, ...siteConfig } = currentConfig;
        const storeDraft = useSiteStylesStore.getState().draft.header;
        const baseSiteConfig = storeDraft ?? (siteConfig as SiteHeaderSlotConfig);
        const updatedConfig: SiteHeaderSlotConfig = { ...baseSiteConfig, [key]: button };
        // Route through the store so the change is captured in
        // captureSiteHistory (undo/redo) and autosaved via persistSlot, then
        // applies on Publish — same flow as colors/fonts.
        useSiteStylesStore.getState().setDraftHeader(updatedConfig);
        setHeaderDraft((prev) => {
          const base = prev ?? currentConfig;
          return { ...base, [key]: button };
        });
      },
      onRevert: (position: 'first' | 'second') => {
        const key = position === 'first' ? 'primaryButton' : 'secondaryButton';
        const originalButton = slots?.header?.[key];
        const storeDraft = useSiteStylesStore.getState().draft.header;
        if (storeDraft) {
          useSiteStylesStore
            .getState()
            .setDraftHeader({ ...storeDraft, [key]: originalButton } as SiteHeaderSlotConfig);
        }
        setHeaderDraft((prev) => {
          if (!prev) return prev;
          return { ...prev, [key]: originalButton };
        });
      },
    }),
    [slots?.header, page?.metadata?.slots?.header?.visible],
  );

  const pageHeaderVisibleOverride = page?.metadata?.slots?.header?.visible;
  const pageFooterVisibleOverride = page?.metadata?.slots?.footer?.visible;
  const { draftHeader: storeHeaderDraft, draftFooter: storeFooterDraft } = useSiteStylesState();
  const headerConfig = useMemo(() => {
    const base = buildHeaderConfig(slots?.header, pageHeaderVisibleOverride);
    return storeHeaderDraft ? { ...base, ...storeHeaderDraft } : base;
  }, [slots?.header, pageHeaderVisibleOverride, storeHeaderDraft]);
  const footerConfig = useMemo(() => {
    const base = buildFooterConfig(slots?.footer, pageFooterVisibleOverride);
    return storeFooterDraft ? { ...base, ...storeFooterDraft } : base;
  }, [slots?.footer, pageFooterVisibleOverride, storeFooterDraft]);
  const previewHeaderSlot = mergePreviewSlot(slots?.header, storeHeaderDraft ?? headerDraft);
  const previewFooterSlot = mergePreviewSlot(slots?.footer, storeFooterDraft ?? footerDraft);

  // Visibility lives in page metadata, so headerDraft/footerDraft don't shadow it.
  const previewHeaderVisible = headerConfig.visible;
  const previewFooterVisible = footerConfig.visible;

  return {
    headerConfig,
    footerConfig,
    previewHeaderSlot,
    previewFooterSlot,
    previewHeaderVisible,
    previewFooterVisible,
    isHeaderEditing,
    isFooterEditing,
    saveHeaderSettings,
    saveFooterSettings,
    setHeaderDraft,
    setFooterDraft,
    headerButtonActions,
  };
}
